import { useEffect, useState } from 'react'
import { resolveOrganization } from 'featurebase-js'
import { LoadingSpinner } from '../auth/components/LoadingSpinner'
import {
  captureIntendedLocation,
  consumeIntendedLocation,
} from '../auth/utils/session'
import { api } from '../services/api'
import { getAppEnv } from '../lib/env'
import {
  portalJwtLoginUrl,
  readReturnToParam,
  safePortalReturnTo,
} from '../lib/featurebase-sso'
import { isFeaturebaseEnabled } from '../lib/feedback-widgets'

/**
 * Featurebase portal SSO landing page (/featurebase/sso).
 *
 * Featurebase sends signed-out portal visitors here. ProtectedRoute has
 * already made sure the engineer is signed in to SupplyScanner (sending them
 * through our login first if not), so we fetch their Featurebase token and
 * hand them straight back to the portal, signed in.
 *
 * This signs people in as portal users. Signing in Featurebase teammates
 * (dashboard admins) via SSO is a separate Enterprise-plan feature we don't
 * use, so test with a normal engineer account.
 */
export function FeaturebaseSsoPage() {
  const appId = import.meta.env.VITE_FEATUREBASE_APP_ID
  const isConfigured = Boolean(appId) && isFeaturebaseEnabled(getAppEnv())
  const [hasFailed, setHasFailed] = useState(false)

  useEffect(() => {
    if (!isConfigured) {
      return
    }

    let isCancelled = false
    const rawReturnTo = readReturnToParam(window.location.search)

    // Remember this page before asking for the token. If the stored login
    // turns out to be stale, the token request sends the engineer back
    // through login, and this brings them back here afterwards instead of
    // to their home page.
    captureIntendedLocation(window.location.pathname + window.location.search)

    Promise.all([resolveOrganization(appId), api.getFeaturebaseJwt()])
      .then(([organization, { jwt }]) => {
        if (isCancelled) return
        // Done — forget this page so the next normal login isn't sent here.
        consumeIntendedLocation()
        const returnTo = safePortalReturnTo(rawReturnTo, organization.slug)
        window.location.replace(portalJwtLoginUrl(organization.slug, jwt, returnTo))
      })
      .catch(() => {
        if (!isCancelled) setHasFailed(true)
      })

    return () => {
      isCancelled = true
    }
  }, [appId, isConfigured])

  if (!isConfigured || hasFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-bold text-gray-900">
            Couldn&rsquo;t sign you in to Featurebase
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Please go back and try again. If it keeps happening, contact support.
          </p>
          <a
            href="/"
            // Giving up on SSO: forget the saved SSO page so the next normal
            // login goes home. (A stale-login failure never shows this button
            // — the app signs the user out by itself — so that resume path
            // keeps its saved page.)
            onClick={() => {
              consumeIntendedLocation()
            }}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-brand-primary px-4 text-sm font-semibold text-[var(--brand-primary-text)]"
          >
            Back to SupplyScanner
          </a>
        </div>
      </div>
    )
  }

  return <LoadingSpinner message="Signing you in to Featurebase..." />
}
